/**
 * OverdueItemMenu Component
 *
 * Popup menu for overdue items with actions:
 * - Pay Now (create transaction for this date)
 * - Skip This Time (skip, but keep recurring schedule)
 * - Skip & Stop Recurring (skip + delete schedule, can re-detect later)
 * - Dismiss (pause, escalates on 2nd dismiss)
 * - Ignore for 1 month / Ignore permanently (shown on escalation)
 */

import { useState } from 'react';
import { ScheduledTransaction } from '@services/db';
import { Modal, Button } from '@/components/shared';
import { formatCurrency as formatCurrencyIntl, formatDate as formatDateIntl } from '@/utils/format';
import { useLanguage } from '@/i18n';
import './OverdueItemMenu.css';

// ============================================================
// Types
// ============================================================

interface OverdueItemMenuProps {
  isOpen: boolean;
  schedule: ScheduledTransaction | null;
  dueDate: Date | null;
  daysOverdue: number;
  currency?: string;  // BUG-074: Account currency for proper symbol display
  onClose: () => void;
  onPay: () => Promise<void>;
  onSkip: () => Promise<void>;
  onSkipAndRemove: () => Promise<void>;
  onDismiss: () => Promise<{ showEscalationPopup: boolean }>;
  onIgnoreMonth: () => Promise<void>;
  onIgnorePermanently: () => Promise<void>;
}

// ============================================================
// Helpers
// ============================================================

// Central locale-aware formatters (utils/format.ts) - BUG-074 safe-currency
// fallback now lives there. Never inline Intl here.
function formatDueDate(date: Date): string {
  return formatDateIntl(date, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatAmount(amount: number, currency?: string): string {
  return formatCurrencyIntl(amount, currency, { minimumFractionDigits: 2 });
}

// ============================================================
// Component
// ============================================================

export function OverdueItemMenu({
  isOpen,
  schedule,
  dueDate,
  daysOverdue,
  currency,
  onClose,
  onPay,
  onSkip,
  onSkipAndRemove,
  onDismiss,
  onIgnoreMonth,
  onIgnorePermanently,
}: OverdueItemMenuProps) {
  const { t } = useLanguage();
  const [loading, setLoading] = useState<string | null>(null);
  const [showIgnoreConfirm, setShowIgnoreConfirm] = useState(false);

  if (!schedule || !dueDate) return null;

  const handleAction = async (action: string, handler: () => Promise<void>) => {
    setLoading(action);
    try {
      await handler();
      onClose();
    } catch (err) {
      console.error('[OverdueItemMenu] Error:', err);
    } finally {
      setLoading(null);
    }
  };

  // Handle dismiss with escalation check
  const handleDismiss = async () => {
    setLoading('dismiss');
    try {
      const result = await onDismiss();
      if (result.showEscalationPopup) {
        // 2nd+ dismiss - show ignore options
        setShowIgnoreConfirm(true);
      } else {
        // 1st dismiss - just close (item is now paused)
        onClose();
      }
    } catch (err) {
      console.error('[OverdueItemMenu] Dismiss error:', err);
    } finally {
      setLoading(null);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('overdue.menu_title')}>
      <div className="overdue-menu">
        {/* Item Details */}
        <div className="overdue-menu-details">
          <div className="overdue-menu-row">
            <span className="overdue-menu-label">{t('transactions.detail.description')}</span>
            <span className="overdue-menu-value">{schedule.description}</span>
          </div>
          <div className="overdue-menu-row">
            <span className="overdue-menu-label">{t('transactions.detail.amount')}</span>
            <span className={`overdue-menu-value ${schedule.type === 'expense' ? 'text-expense' : 'text-income'}`}>
              {schedule.type === 'expense' ? '-' : '+'}{formatAmount(schedule.amount, currency)}
            </span>
          </div>
          <div className="overdue-menu-row">
            <span className="overdue-menu-label">{t('overdue.due_date')}</span>
            <span className="overdue-menu-value overdue-menu-date">
              {formatDueDate(dueDate)}
              <span className="overdue-badge">{t('overdue.days_overdue', { days: daysOverdue })}</span>
            </span>
          </div>
          <div className="overdue-menu-row">
            <span className="overdue-menu-label">{t('overdue.frequency')}</span>
            <span className="overdue-menu-value">{t(`transactions.frequency.${schedule.frequency}`)}</span>
          </div>
        </div>

        {/* Actions */}
        {!showIgnoreConfirm ? (
          <div className="overdue-menu-actions">
            <Button
              variant="primary"
              onClick={() => handleAction('pay', onPay)}
              disabled={!!loading}
              className="overdue-action-btn pay-btn"
            >
              {loading === 'pay' ? t('overdue.pay_loading') : t('overdue.pay_now')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => handleAction('skip', onSkip)}
              disabled={!!loading}
              className="overdue-action-btn"
            >
              {loading === 'skip' ? t('transactions.detail.skipping') : t('transactions.detail.skip_this')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => handleAction('skipAndRemove', onSkipAndRemove)}
              disabled={!!loading}
              className="overdue-action-btn skip-remove-btn"
            >
              {loading === 'skipAndRemove' ? t('overdue.removing') : t('overdue.skip_remove')}
            </Button>
            <button
              className="overdue-link-btn"
              onClick={handleDismiss}
              disabled={!!loading}
            >
              {loading === 'dismiss' ? t('overdue.dismissing') : t('overdue.dismiss')}
            </button>
          </div>
        ) : (
          <div className="overdue-menu-ignore">
            <p className="ignore-title">{t('overdue.ignore_title')}</p>
            <p className="ignore-description">
              {t('overdue.ignore_desc', { name: schedule.description })}
            </p>
            <div className="ignore-actions">
              <Button
                variant="secondary"
                onClick={() => handleAction('ignoreMonth', onIgnoreMonth)}
                disabled={!!loading}
              >
                {loading === 'ignoreMonth' ? t('overdue.ignoring') : t('overdue.ignore_month')}
              </Button>
              <Button
                variant="danger"
                onClick={() => handleAction('ignorePermanently', onIgnorePermanently)}
                disabled={!!loading}
              >
                {loading === 'ignorePermanently' ? t('overdue.ignoring') : t('overdue.ignore_permanently')}
              </Button>
              <Button
                variant="ghost"
                onClick={() => setShowIgnoreConfirm(false)}
                disabled={!!loading}
              >
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default OverdueItemMenu;
